
import React, { useState, useMemo, useEffect, useRef, Suspense, lazy } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { Header } from './components/Header';
import { FilterState } from './types';
import { Column } from './components/Column';
import { Card } from './components/Card';
import { StatsBar } from './components/StatsBar';
import { BoardSkeleton } from './components/BoardSkeleton';
import { ModalErrorBoundary } from './components/ModalErrorBoundary';
import { OfflineBanner } from './components/OfflineBanner';
import { EmployeeModalSkeleton } from './components/EmployeeModalSkeleton';
import { SettingsPanelSkeleton } from './components/SettingsPanelSkeleton';
import { LoginView } from './components/LoginView';
import { AuthModal } from './components/AuthModal';
import { VersionInfoModal } from './components/VersionInfoModal';
import { ChangePasswordModal } from './components/ChangePasswordModal';
import { ConfirmDeleteModal } from './components/ConfirmDeleteModal';
import { UpdateBanner } from './components/UpdateBanner';
import { useAppVersion } from './hooks/useAppVersion';
import { useNetworkTime } from './hooks/useNetworkTime';
import { useAuthSession } from './hooks/useAuthSession';
import { useBoardData } from './hooks/useBoardData';
import { useBoardDnD, boardCollisionDetection, dropAnimation } from './hooks/useBoardDnD';
import { useBoardScroll } from './hooks/useBoardScroll';
import { useModals } from './hooks/useModals';
import { usePdfExport } from './hooks/usePdfExport';
import { useFilteredEmployees, useEmployeeStats } from './hooks/useFilteredEmployees';
import { INITIAL_FILTERS } from './hooks/appDefaults';
import { saveColumns } from './services/rhDataService';
import { Company, User } from './types';
import { canCreateEmployee, canEditEmployee, canDeleteEmployee } from './utils/permissions';

const EmployeeModal = lazy(() => import('./components/EmployeeModal').then(m => ({ default: m.EmployeeModal })));
const AppSettingsPanel = lazy(() =>
  import('./components/AppSettingsPanel').then(m => ({ default: m.AppSettingsPanel })),
);
const DashboardModal = lazy(() => import('./components/DashboardModal').then(m => ({ default: m.DashboardModal })));
const HRModal = lazy(() => import('./components/HRModal').then(m => ({ default: m.HRModal })));
const AbsencesModal = lazy(() => import('./components/AbsencesModal').then(m => ({ default: m.AbsencesModal })));

const ModalLoader = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
    <div className="bg-white rounded-xl p-4 shadow-xl flex items-center gap-3">
      <div className="w-5 h-5 border-2 border-primary-accent/30 border-t-primary-accent rounded-full animate-spin" />
      <span className="text-sm font-medium text-slate-600">Carregando...</span>
    </div>
  </div>
);

/** Escolhe skeleton conforme modal aberto — evita loader genérico nos modais pesados. */
function ModalSuspenseFallback(props: {
  isEmployeeOpen: boolean;
  isSettingsOpen: boolean;
}) {
  if (props.isEmployeeOpen) return <EmployeeModalSkeleton />;
  if (props.isSettingsOpen) return <SettingsPanelSkeleton />;
  return <ModalLoader />;
}

export default function App() {
  const { hasUpdate } = useAppVersion();
  const { timeOffset, isTimeSynced, getVerifiedDate } = useNetworkTime();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(new Set());
  const [zoomLevel, setZoomLevel] = useState(100);

  const boardLoadRef = useRef<(user: User, company: Company) => Promise<boolean>>(async () => false);
  const boardResetRef = useRef<() => void>(() => {});

  const auth = useAuthSession({
    onLoginSuccess: (user, company) => boardLoadRef.current(user, company),
    onLogout: () => boardResetRef.current(),
    postgresApiUrl: '',
  });

  const board = useBoardData({
    isAuthenticated: auth.isAuthenticated,
    currentCompany: auth.currentCompany,
    currentUser: auth.currentUser,
    getVerifiedDate,
  });

  useEffect(() => {
    boardLoadRef.current = board.loadCompanyAfterLogin;
    boardResetRef.current = board.resetBoard;
  });

  const modals = useModals();

  const boardScale = zoomLevel / 100;
  const boardZoomStyle =
    boardScale !== 1
      ? {
          transform: `scale(${boardScale})`,
          transformOrigin: 'top left',
          width: `${100 / boardScale}%`,
          minHeight: `${100 / boardScale}%`,
        }
      : undefined;

  useEffect(() => {
    if (modals.isSettingsOpen && auth.isAuthenticated) {
      void board.refreshMondayLogs();
    }
  }, [modals.isSettingsOpen, auth.isAuthenticated, board.refreshMondayLogs]);

  const scroll = useBoardScroll(auth.isAuthenticated, board.columns, zoomLevel);

  const userPerms = auth.currentUser?.permissions;
  const allowCreateEmployee = canCreateEmployee(userPerms);
  const allowEditEmployee = canEditEmployee(userPerms);
  const allowDeleteEmployee = canDeleteEmployee(userPerms);

  const dnd = useBoardDnD({
    items: board.items,
    setItems: board.setItems,
    columns: board.columns,
    columnKeyMap: board.columnKeyMap,
    afastamentoDates: board.afastamentoDates,
    setAfastamentoDates: board.setAfastamentoDates,
    currentCompany: auth.currentCompany,
    getSB: board.getSB,
    persistSettingIfAuthenticated: board.persistSettingIfAuthenticated,
    getVerifiedDate,
    addLog: board.addLog,
    broadcastNotifications: board.broadcastNotifications,
    canEditEmployees: allowEditEmployee,
  });

  const pdf = usePdfExport({
    items: board.items,
    columns: board.columns,
    currentCompany: auth.currentCompany,
    onReportGenerated: (title, message) => board.addNotification(title, message, 'info'),
    referenceDate: getVerifiedDate(),
  });

  const todayStr = useMemo(() => {
    const today = getVerifiedDate();
    return `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }, [getVerifiedDate]);

  const filteredItems = useFilteredEmployees(board.items, board.columns, search, filters, todayStr, timeOffset);
  const stats = useEmployeeStats(board.items, board.columns, board.hiringLimits, timeOffset);
  const showBoardSkeleton = auth.isLoading || board.isBoardLoading;

  /** Sanplast: 3 setores por coluna, empilhamento compacto. Demais: 3 turnos soltos + empilhamento de 3. */
  const boardLayout = useMemo(() => {
    const isSanplast = auth.currentCompany === 'Sanplast';
    const stackSize = 3;
    const standaloneCount = board.isAdministrative || isSanplast ? 0 : 3;

    const standaloneColumns = board.columns.slice(0, standaloneCount);
    const stackedSource = board.isAdministrative
      ? board.columns
      : board.columns.slice(standaloneCount);

    const stackedGroups = stackedSource.reduce(
      (acc, col, i) => {
        if (i % stackSize === 0) acc.push([]);
        acc[acc.length - 1].push(col);
        return acc;
      },
      [] as { id: string; title: string; color?: string }[][],
    );

    return { isSanplast, standaloneColumns, stackedGroups };
  }, [auth.currentCompany, board.isAdministrative, board.columns]);

  const toggleCollapse = (id: string) =>
    setCollapsedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const loadingOverlay = (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-950/90 backdrop-blur-sm animate-in fade-in transition-all duration-300">
      <div className="flex flex-col items-center justify-center gap-5">
        <div className="relative flex items-center justify-center">
          <div className="absolute inset-0 border-4 border-[#5C8FF0]/30 rounded-full blur-[2px]" />
          <div className="w-14 h-14 rounded-full border-4 border-transparent border-t-[#5C8FF0] animate-spin drop-shadow-[0_0_10px_rgba(92,143,240,0.6)]" />
          <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-[#A3C2FF] animate-spin animate-reverse drop-shadow-[0_0_8px_rgba(163,194,255,0.5)]" />
        </div>
        <p className="text-white text-base font-bold tracking-[0.2em] animate-pulse flex items-center gap-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]">
          <span className="material-symbols-outlined !text-[20px] text-[#A3C2FF]">motion_photos_on</span>
          CARREGANDO SISTEMA
        </p>
      </div>
    </div>
  );

  if (!auth.isAuthenticated) {
    if (auth.isRestoringSession) {
      return loadingOverlay;
    }

    return (
      <>
        <UpdateBanner visible={hasUpdate} />
        {board.notifications.length > 0 && (
          <div className="fixed top-3 right-3 z-[9999] flex flex-col gap-2 max-w-sm">
            {board.notifications.slice(0, 3).map((n) => (
              <div
                key={n.id}
                className={`rounded-lg border px-3 py-2 text-xs shadow-lg ${
                  n.type === 'delete'
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : n.type === 'add'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}
              >
                <p className="font-semibold">{n.title}</p>
                <p>{n.message}</p>
              </div>
            ))}
          </div>
        )}
        {auth.isLoading && loadingOverlay}
        <LoginView onSelectCompany={auth.handleSelectCompany} />
        {auth.currentCompany && (
          <AuthModal
            company={auth.currentCompany}
            isOpen={auth.isAuthModalOpen}
            onClose={() => auth.setIsAuthModalOpen(false)}
            onSuccess={auth.handleAuthSuccess}
            postgresConfig={board.postgresConfig}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden animate-in fade-in duration-700">
      <OfflineBanner visible={board.connectionState === 'offline'} />
      <UpdateBanner visible={hasUpdate} />
      <Header
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onFilterChange={setFilters}
        onOpenSettings={() => modals.setIsSettingsOpen(true)}
        onOpenChangePassword={() => modals.setIsChangePasswordOpen(true)}
        onAddEmployee={allowCreateEmployee ? modals.openNewEmployee : undefined}
        onOpenDashboard={() => modals.setIsDashboardOpen(true)}
        onOpenHR={() => modals.setIsHRModalOpen(true)}
        onOpenAbsences={() => modals.setIsAbsencesModalOpen(true)}
        onDownloadEmployeesReport={pdf.handleDownloadEmployeesReport}
        onDownloadAbsencesReport={pdf.handleDownloadAbsencesReport}
        onLogout={auth.handleLogout}
        userPermissions={auth.currentUser?.permissions}
        currentUser={auth.currentUser}
        notifications={board.notifications}
        onDismissNotification={(id) => void board.dismissNotification(id)}
        onClearNotifications={() => void board.clearNotifications()}
        isSynced={isTimeSynced}
        isDbConnected={board.isDbConnected}
        timeOffset={timeOffset}
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        currentCompany={auth.currentCompany}
        onOpenVersionInfo={() => modals.setIsVersionInfoOpen(true)}
      />
      <main className="flex flex-1 flex-col overflow-hidden relative">
        <DndContext
          sensors={dnd.sensors}
          collisionDetection={boardCollisionDetection}
          onDragStart={dnd.handleDragStart}
          onDragOver={dnd.handleDragOver}
          onDragEnd={dnd.handleDragEnd}
        >
          <div
            ref={scroll.boardScrollRef}
            className="flex-1 min-h-[500px] min-w-0 h-full overflow-x-auto overflow-y-hidden px-3 md:px-4 pt-4 pb-12 custom-scrollbar scroll-smooth"
          >
            {showBoardSkeleton ? (
              <BoardSkeleton columnCount={board.isAdministrative ? 4 : 6} />
            ) : (
            <div
              ref={scroll.boardScrollContentRef}
              className={`flex gap-3 min-w-max h-full items-stretch ${
                boardLayout.isSanplast ? 'pb-0' : 'pb-3'
              }`}
              style={boardZoomStyle}
            >
              {boardLayout.standaloneColumns.map((col) => (
                <Column
                  key={col.id}
                  id={col.id}
                  title={col.title}
                  color={col.color}
                  items={filteredItems[col.id] || []}
                  roleColors={board.roleColors}
                  isCollapsed={collapsedColumns.has(col.id)}
                  onToggleCollapse={() => toggleCollapse(col.id)}
                  onEdit={allowEditEmployee ? modals.openEditEmployee : undefined}
                  onDelete={allowDeleteEmployee ? (id) => modals.setEmployeeToDelete(id) : undefined}
                  canDragEmployees={allowEditEmployee}
                  referenceDate={getVerifiedDate()}
                />
              ))}
              {boardLayout.stackedGroups
                .map((group, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-col w-auto shrink-0 min-h-0 ${
                      boardLayout.isSanplast ? 'gap-1 h-full' : 'gap-4 h-full'
                    }`}
                  >
                    {group.map((col) => (
                      <Column
                        key={col.id}
                        id={col.id}
                        title={col.title}
                        color={col.color}
                        items={filteredItems[col.id] || []}
                        roleColors={board.roleColors}
                        isStacked
                        compactStack={boardLayout.isSanplast}
                        isCollapsed={collapsedColumns.has(col.id)}
                        onToggleCollapse={() => toggleCollapse(col.id)}
                        onEdit={allowEditEmployee ? modals.openEditEmployee : undefined}
                        onDelete={allowDeleteEmployee ? (id) => modals.setEmployeeToDelete(id) : undefined}
                        canDragEmployees={allowEditEmployee}
                        referenceDate={getVerifiedDate()}
                      />
                    ))}
                  </div>
                ))}
            </div>
            )}
          </div>
          <DragOverlay dropAnimation={dropAnimation}>
            {dnd.activeItem ? (
              <Card
                employee={dnd.activeItem}
                roleColor={board.roleColors[dnd.activeItem.role]}
                referenceDate={getVerifiedDate()}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
        <StatsBar
          total={stats.total}
          away={stats.away}
          probation45={stats.probation45}
          probation90={stats.probation90}
          hiring={stats.hiring}
          absencesMonth={stats.absencesMonth}
          boardCanScrollLeft={scroll.boardCanScrollLeft}
          boardCanScrollRight={scroll.boardCanScrollRight}
          onBoardScrollLeft={() => scroll.scrollBoardHorizontal('left')}
          onBoardScrollRight={() => scroll.scrollBoardHorizontal('right')}
        />
        <ModalErrorBoundary>
          <Suspense
            fallback={
              <ModalSuspenseFallback
                isEmployeeOpen={modals.isModalOpen}
                isSettingsOpen={modals.isSettingsOpen}
              />
            }
          >
            <EmployeeModal
            isOpen={modals.isModalOpen}
            onClose={modals.closeEmployeeModal}
            onSave={(data) =>
              board.handleSaveEmployee(data, modals.editingEmployee, modals.closeEmployeeModal)
            }
            initialData={modals.editingEmployee}
            availableRoles={board.availableRoles}
            columns={board.columns}
            costCenters={board.costCenters}
            currentColumnId={
              modals.editingEmployee
                ? Object.keys(board.items).find((key) =>
                    board.items[key].some((e) => e.id === modals.editingEmployee!.id),
                  )
                : undefined
            }
            referenceDate={getVerifiedDate()}
            currentUser={auth.currentUser}
          />
          <AppSettingsPanel
            isOpen={modals.isSettingsOpen}
            onClose={() => modals.setIsSettingsOpen(false)}
            board={board}
            currentCompany={auth.currentCompany}
            currentUser={auth.currentUser}
            onLogout={auth.handleLogout}
          />
          {auth.currentUser?.permissions.dashboard && (
            <DashboardModal
              isOpen={modals.isDashboardOpen}
              onClose={() => modals.setIsDashboardOpen(false)}
              items={board.items}
              columns={board.columns}
              hiringLimits={board.hiringLimits}
              isAdministrative={board.isAdministrative}
              afastamentoDates={board.afastamentoDates}
              referenceDate={getVerifiedDate()}
            />
          )}
          {auth.currentUser?.permissions.hr && (
            <HRModal
              isOpen={modals.isHRModalOpen}
              onClose={() => modals.setIsHRModalOpen(false)}
              columns={board.columns}
              hiringLimits={board.hiringLimits}
              onUpdateHiringLimit={(id, l) => {
                const newLimits = { ...board.hiringLimits, [id]: l };
                board.setHiringLimits(newLimits);
                const sb = board.getSB();
                if (sb && board.currentCompanyId) {
                  saveColumns(sb, board.currentCompanyId, board.columns, newLimits).catch(console.warn);
                }
              }}
              isAdministrative={board.isAdministrative}
            />
          )}
          {auth.currentUser?.permissions.absences && (
            <AbsencesModal
              isOpen={modals.isAbsencesModalOpen}
              onClose={() => modals.setIsAbsencesModalOpen(false)}
              items={board.items}
              columns={board.columns}
              onUpdateAbsence={board.handleUpdateAbsence}
              referenceDate={getVerifiedDate()}
            />
          )}
          </Suspense>
        </ModalErrorBoundary>
        <VersionInfoModal
          isOpen={modals.isVersionInfoOpen}
          onClose={() => modals.setIsVersionInfoOpen(false)}
          info={board.appVersionInfo}
        />
        <ChangePasswordModal
          isOpen={modals.isChangePasswordOpen}
          onClose={() => modals.setIsChangePasswordOpen(false)}
          postgresApiUrl={board.postgresConfig.url}
        />
      </main>

      {modals.employeeToDelete && (
        <ConfirmDeleteModal
          isOpen={Boolean(modals.employeeToDelete)}
          title="Excluir Colaborador"
          message={
            <>
              Tem certeza que deseja remover este colaborador? <br />
              Esta ação é <span className="text-status-red font-semibold">irreversível</span> e todos os
              dados serão apagados do sistema de forma permanente.
            </>
          }
          onCancel={() => modals.setEmployeeToDelete(null)}
          onConfirm={async () => {
            if (!modals.employeeToDelete) return false;
            await board.confirmDelete(modals.employeeToDelete, () => modals.setEmployeeToDelete(null));
            return true;
          }}
        />
      )}
    </div>
  );
}
